import React from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import { ArrowLeft, ChevronRight, Download, Eye, Printer, TrendingUp, Trophy } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

const toMonthYYYYMM = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

function formatNumber(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return 0;
  return n;
}

function fmtDisplay(val) {
  return formatNumber(val).toLocaleString("en-IN");
}

function formatPercent(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0.00%";
  return `${n.toFixed(2)}%`;
}

function formatPercentShort(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n.toFixed(1)}%`;
}

function getConversionPercent(totals) {
  return formatNumber(
    totals?.conversion_percent_this_month ?? totals?.conversion_percent ?? 0
  );
}

function buildYearOptions(centerYear) {
  const y = Number(centerYear);
  const base = Number.isFinite(y) ? y : new Date().getFullYear();
  const start = Math.max(2000, base - 5);
  const end = Math.min(2100, base + 5);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function getDailyRowTimestamp(row) {
  const raw = row?.date || row?.date_label;
  if (!raw) return 0;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

  const dmy = String(raw).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  return 0;
}

function getLeadDateParamFromDailyRow(row, selectedMonth) {
  const raw = String(row?.date || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const label = String(row?.date_label || "").trim();
  let m = label.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  m = label.match(/^(\d{2})-(\d{2})$/);
  if (m && /^\d{4}-\d{2}$/.test(String(selectedMonth || ""))) {
    const [y, mm] = String(selectedMonth).split("-");
    return `${y}-${mm}-${m[1]}`;
  }
  return null;
}

const MONTHS = [
  { id: "01", short: "Jan", full: "January" },
  { id: "02", short: "Feb", full: "February" },
  { id: "03", short: "Mar", full: "March" },
  { id: "04", short: "Apr", full: "April" },
  { id: "05", short: "May", full: "May" },
  { id: "06", short: "Jun", full: "June" },
  { id: "07", short: "Jul", full: "July" },
  { id: "08", short: "Aug", full: "August" },
  { id: "09", short: "Sep", full: "September" },
  { id: "10", short: "Oct", full: "October" },
  { id: "11", short: "Nov", full: "November" },
  { id: "12", short: "Dec", full: "December" },
];

const QUARTERS = [
  { key: "Q1", months: ["01", "02", "03"] },
  { key: "Q2", months: ["04", "05", "06"] },
  { key: "Q3", months: ["07", "08", "09"] },
  { key: "Q4", months: ["10", "11", "12"] },
];

const MONTH_BY_ID = Object.fromEntries(MONTHS.map((m) => [m.id, m]));

function buildMonthOptions(centerMonth) {
  const now = new Date();
  const cy = centerMonth ? parseInt(centerMonth.split("-")[0], 10) : now.getFullYear();
  const opts = [];
  for (let y = cy + 2; y >= cy - 3; y--) {
    for (let m = 12; m >= 1; m--) {
      const id = String(m).padStart(2, "0");
      opts.push({ value: `${y}-${id}`, label: `${MONTH_BY_ID[id]?.full || id} ${y}` });
    }
  }
  return opts;
}

export default function LeadMonthlyFunnelReport() {
  const navigate = useNavigate();
  const { month: monthParam } = useParams();
  const { userData } = useAuthStore();
  const organizationId = userData?.organization?.organization_id;
  const organizationName =
    userData?.organization?.organization_name ||
    userData?.organization?.organization_short_name ||
    "";
  const monthParamValid = /^\d{4}-\d{2}$/.test(String(monthParam || ""));

  const [month, setMonth] = React.useState(() =>
    monthParamValid ? String(monthParam) : toMonthYYYYMM(new Date())
  );
  const [year, setYear] = React.useState(() => new Date().getFullYear());
  const [loading, setLoading] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [errorText, setErrorText] = React.useState(null);
  const [yearRowsLoading, setYearRowsLoading] = React.useState(false);
  const [yearRows, setYearRows] = React.useState([]);

  const fetchReport = async (monthToUse) => {
    if (!organizationId) return;

    setLoading(true);
    setErrorText(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${MAIN_URL}/api/ems/leads-monthly-summary`,
        {
          organization_id: organizationId,
          month: monthToUse,
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      setReport(res?.data?.data || null);
    } catch (err) {
      console.error("LeadMonthlyFunnelReport error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to fetch report";
      setErrorText(msg);
      toast.error(msg);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!monthParamValid) return;
    if (organizationId) fetchReport(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, monthParamValid, month]);

  React.useEffect(() => {
    if (monthParamValid) {
      setMonth(String(monthParam));
    }
  }, [monthParamValid, monthParam]);

  React.useEffect(() => {
    if (monthParamValid) return;
    if (!organizationId || !year) return;

    let isActive = true;
    const loadYearRows = async () => {
      setYearRowsLoading(true);
      setErrorText(null);
      try {
        const token = localStorage.getItem("token");
        const requests = MONTHS.map((m) => {
          const ym = `${year}-${m.id}`;
          return axios
            .post(
              `${MAIN_URL}/api/ems/leads-monthly-summary`,
              { organization_id: organizationId, month: ym },
              {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              }
            )
            .then((res) => ({
              month: ym,
              short: m.short,
              full: m.full,
              totals: res?.data?.data?.totals || {},
            }))
            .catch(() => ({
              month: ym,
              short: m.short,
              full: m.full,
              totals: {},
            }));
        });
        const rows = await Promise.all(requests);
        if (!isActive) return;
        setYearRows(rows);
      } catch (err) {
        if (!isActive) return;
        setYearRows([]);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch month-wise report";
        setErrorText(msg);
      } finally {
        if (!isActive) return;
        setYearRowsLoading(false);
      }
    };

    loadYearRows();
    return () => {
      isActive = false;
    };
  }, [organizationId, year, monthParamValid]);

  const yearTotals = React.useMemo(() => {
    return (yearRows || []).reduce(
      (acc, row) => {
        const t = row?.totals || {};
        acc.leads += formatNumber(t.leads_this_month);
        acc.qualified += formatNumber(t.qualified_this_month);
        acc.demo += formatNumber(t.demo_this_month);
        acc.enrolled += formatNumber(t.enrollment_this_month);
        return acc;
      },
      { leads: 0, qualified: 0, demo: 0, enrolled: 0 }
    );
  }, [yearRows]);

  const yearConversionPct = React.useMemo(() => {
    if (yearTotals.leads <= 0) return 0;
    return (yearTotals.enrolled / yearTotals.leads) * 100;
  }, [yearTotals]);

  const bestMonthRow = React.useMemo(() => {
    if (!yearRows?.length) return null;
    let best = null;
    yearRows.forEach((row) => {
      const enrolled = formatNumber(row.totals?.enrollment_this_month);
      if (!best || enrolled > formatNumber(best.totals?.enrollment_this_month)) {
        best = row;
      }
    });
    if (!best || formatNumber(best.totals?.enrollment_this_month) <= 0) return null;
    return best;
  }, [yearRows]);

  const rowsByMonthId = React.useMemo(() => {
    const map = {};
    (yearRows || []).forEach((row) => {
      const mm = String(row.month || "").split("-")[1];
      if (mm) map[mm] = row;
    });
    return map;
  }, [yearRows]);

  const downloadYearPdf = React.useCallback(async () => {
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({ unit: "pt", format: "a4" });

      const marginX = 40;
      let yPos = 48;
      const lineH = 16;

      doc.setFontSize(14);
      doc.text(
        `Lead Monthly Report (${year})${organizationName ? ` — ${organizationName}` : ""}`,
        marginX,
        yPos
      );
      yPos += 18;

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("Month-by-month lead funnel and conversion", marginX, yPos);
      doc.setTextColor(0);
      yPos += 20;

      doc.text(
        `Totals: ${fmtDisplay(yearTotals.leads)} leads · ${fmtDisplay(yearTotals.qualified)} qualified · ${fmtDisplay(yearTotals.demo)} demos · ${fmtDisplay(yearTotals.enrolled)} enrolled · ${formatPercentShort(yearConversionPct)} conversion`,
        marginX,
        yPos
      );
      yPos += 22;

      const cols = {
        month: marginX,
        leads: 200,
        qualified: 270,
        demos: 340,
        enrolled: 410,
        conversion: 480,
      };

      doc.setFontSize(9);
      doc.setFont(undefined, "bold");
      doc.text("Month", cols.month, yPos);
      doc.text("New Leads", cols.leads, yPos, { align: "right" });
      doc.text("Qualified", cols.qualified, yPos, { align: "right" });
      doc.text("Demos", cols.demos, yPos, { align: "right" });
      doc.text("Enrolled", cols.enrolled, yPos, { align: "right" });
      doc.text("Conversion", cols.conversion, yPos, { align: "right" });
      doc.setFont(undefined, "normal");
      yPos += 10;
      doc.setDrawColor(180);
      doc.line(marginX, yPos, 555, yPos);
      yPos += 14;

      QUARTERS.forEach((q) => {
        if (yPos > 760) {
          doc.addPage();
          yPos = 48;
        }
        doc.setFont(undefined, "bold");
        doc.text(`${q.key} - ${year}`, marginX, yPos);
        doc.setFont(undefined, "normal");
        yPos += lineH;

        q.months.forEach((mm) => {
          if (yPos > 760) {
            doc.addPage();
            yPos = 48;
          }
          const row = rowsByMonthId[mm];
          const meta = MONTH_BY_ID[mm];
          const t = row?.totals || {};
          const conv = getConversionPercent(t);
          doc.text(meta?.full || mm, cols.month, yPos);
          doc.text(String(fmtDisplay(t.leads_this_month)), cols.leads, yPos, {
            align: "right",
          });
          doc.text(
            String(fmtDisplay(t.qualified_this_month)),
            cols.qualified,
            yPos,
            { align: "right" }
          );
          doc.text(String(fmtDisplay(t.demo_this_month)), cols.demos, yPos, {
            align: "right",
          });
          doc.text(
            String(fmtDisplay(t.enrollment_this_month)),
            cols.enrolled,
            yPos,
            { align: "right" }
          );
          doc.text(formatPercentShort(conv), cols.conversion, yPos, {
            align: "right",
          });
          yPos += lineH;
        });
        yPos += 4;
      });

      doc.save(`lead-monthly-report-${year}.pdf`);
    } catch (err) {
      console.error("Lead monthly PDF error:", err);
      toast.error("Failed to generate PDF");
    }
  }, [
    organizationName,
    rowsByMonthId,
    year,
    yearConversionPct,
    yearTotals,
  ]);

  const totals = report?.totals || {};
  const dailyRows = React.useMemo(() => {
    const rows = report?.daily?.length ? [...report.daily] : [];
    const now = new Date();
    const selected = String(month || "").trim();
    const selectedMatch = selected.match(/^(\d{4})-(\d{2})$/);
    const isCurrentMonth =
      !!selectedMatch &&
      Number(selectedMatch[1]) === now.getFullYear() &&
      Number(selectedMatch[2]) === now.getMonth() + 1;

    const todayEnd = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    ).getTime();

    const filtered = isCurrentMonth
      ? rows.filter((row) => {
          const ts = getDailyRowTimestamp(row);
          return ts > 0 && ts <= todayEnd;
        })
      : rows;

    return filtered.sort(
      (a, b) => getDailyRowTimestamp(b) - getDailyRowTimestamp(a)
    );
  }, [report, month]);

  const [selYear, selMonth] = String(month || "").split("-");
  const monthShort = React.useMemo(() => {
    const map = Object.fromEntries(MONTHS.map((m) => [m.id, m.short]));
    return map[selMonth] || selMonth || "-";
  }, [selMonth]);

  const monthFullName = MONTH_BY_ID[selMonth]?.full || selMonth || "-";

  const downloadMonthPdf = React.useCallback(async () => {
    if (!report) return;
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const mx = 40;
      let yp = 48;
      doc.setFontSize(14);
      doc.text(
        `Lead Monthly Funnel Report — ${month}${organizationName ? ` · ${organizationName}` : ""}`,
        mx, yp
      );
      yp += 22;
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("Daily leads, stage counts, demos, enrollments and conversion", mx, yp);
      doc.setTextColor(0);
      yp += 22;
      doc.text(
        `Leads: ${fmtDisplay(totals.leads_this_month)} · Qualified: ${fmtDisplay(totals.qualified_this_month)} · Demo: ${fmtDisplay(totals.demo_this_month)} · Enrolled: ${fmtDisplay(totals.enrollment_this_month)} · Conversion: ${formatPercent(getConversionPercent(totals))}`,
        mx, yp
      );
      yp += 24;
      const c = { date: mx, leads: 175, qual: 238, unqual: 305, spam: 356, demo: 403, enr: 455, conv: 530 };
      doc.setFontSize(9);
      doc.setFont(undefined, "bold");
      doc.text("Date", c.date, yp);
      doc.text("Leads", c.leads, yp, { align: "right" });
      doc.text("Qualified", c.qual, yp, { align: "right" });
      doc.text("Unqualified", c.unqual, yp, { align: "right" });
      doc.text("Spam", c.spam, yp, { align: "right" });
      doc.text("Demo", c.demo, yp, { align: "right" });
      doc.text("Enrolled", c.enr, yp, { align: "right" });
      doc.text("Conv%", c.conv, yp, { align: "right" });
      doc.setFont(undefined, "normal");
      yp += 10;
      doc.setDrawColor(180);
      doc.line(mx, yp, 555, yp);
      yp += 14;
      dailyRows.forEach((row) => {
        if (yp > 760) { doc.addPage(); yp = 48; }
        const rowLeads = formatNumber(row.leads ?? 0);
        const rowQual = formatNumber(row.qualified ?? row.stage_counts?.Qualified ?? 0);
        const rowUnqual = formatNumber(row.unqualified ?? row.stage_counts?.Unqualified ?? 0);
        const rowSpam = formatNumber(row.spam ?? row.stage_counts?.Spam ?? 0);
        const rowDemo = formatNumber(row.demo ?? 0);
        const rowEnr = formatNumber(row.enrollment ?? row.admission_enrollment ?? row.stage_counts?.Enrolled ?? 0);
        const rowConv = formatNumber(row.conversion_percent ?? 0);
        doc.text(String(row.date_label || row.date || "-"), c.date, yp);
        doc.text(String(fmtDisplay(rowLeads)), c.leads, yp, { align: "right" });
        doc.text(String(fmtDisplay(rowQual)), c.qual, yp, { align: "right" });
        doc.text(String(fmtDisplay(rowUnqual)), c.unqual, yp, { align: "right" });
        doc.text(String(fmtDisplay(rowSpam)), c.spam, yp, { align: "right" });
        doc.text(String(fmtDisplay(rowDemo)), c.demo, yp, { align: "right" });
        doc.text(String(fmtDisplay(rowEnr)), c.enr, yp, { align: "right" });
        doc.text(formatPercentShort(rowConv), c.conv, yp, { align: "right" });
        yp += 14;
      });
      doc.save(`lead-funnel-${month}.pdf`);
    } catch (err) {
      console.error("Lead monthly detail PDF error:", err);
      toast.error("Failed to generate PDF");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, month, totals, dailyRows, organizationName]);

  const renderMonthRow = (row) => {
    if (!row) return null;
    const t = row.totals || {};
    const leads = formatNumber(t.leads_this_month);
    const qualified = formatNumber(t.qualified_this_month);
    const demos = formatNumber(t.demo_this_month);
    const enrolled = formatNumber(t.enrollment_this_month);
    const conv = getConversionPercent(t);
    const barWidth = Math.min(100, Math.max(0, conv));

    return (
      <tr
        key={row.month}
        className="ems-lmr-month-row"
        onClick={() => navigate(`/leads-monthly-funnel-report/${row.month}`)}
        style={{ cursor: "pointer" }}
      >
        <td className="ems-lmr-month">{row.full}</td>
        <td className={`ems-num ems-lmr-num${leads > 0 ? "" : " is-zero"}`}>
          {fmtDisplay(leads)}
        </td>
        <td className={`ems-num ems-lmr-num${qualified > 0 ? "" : " is-zero"}`}>
          {fmtDisplay(qualified)}
        </td>
        <td className={`ems-num ems-lmr-num${demos > 0 ? "" : " is-zero"}`}>
          {fmtDisplay(demos)}
        </td>
        <td
          className={`ems-num ems-lmr-enrolled${enrolled > 0 ? "" : " is-zero"}`}
        >
          {fmtDisplay(enrolled)}
        </td>
        <td className="ems-num">
          <div className="ems-lmr-conv-cell">
            <div className="ems-conv">
              <div className="ems-conv-track">
                <div
                  className="ems-conv-fill"
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <span className="ems-conv-val">{formatPercentShort(conv)}</span>
            </div>
            <button
              type="button"
              className="ems-lmr-go"
              aria-label={`Open ${row.full} details`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/leads-monthly-funnel-report/${row.month}`);
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  if (!monthParamValid) {
    return (
      <div className="ems-dashboard ems-fade-up" id="ems-lmr-print">
        <div className="ems-page-head">
          <div>
            <h1 className="ems-page-title">Lead Monthly Report</h1>
            <div className="ems-page-sub">
              Month-by-month lead funnel and conversion
            </div>
          </div>
          <div className="ems-head-actions">
            <button
              type="button"
              className="ems-btn ems-ghost"
              onClick={() => window.print()}
              disabled={yearRowsLoading}
            >
              <Printer size={15} />
              Print
            </button>
            <button
              type="button"
              className="ems-btn ems-primary"
              onClick={downloadYearPdf}
              disabled={yearRowsLoading || !yearRows?.length}
            >
              <Download size={15} />
              Download PDF
            </button>
          </div>
        </div>

        <div className="ems-section-divider" />

        <div className="ems-card ems-year-card">
          <div className="ems-year-field">
            <label htmlFor="lmr-year">Report Year</label>
            <select
              id="lmr-year"
              className="ems-select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {buildYearOptions(year).map((y) => (
                <option key={String(y)} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="ems-year-stats">
            <div className="ems-year-stat">
              <span className="ys-v ems-tnum">{fmtDisplay(yearTotals.leads)}</span>
              <span className="ys-l">New Leads</span>
            </div>
            <div className="ems-year-stat">
              <span className="ys-v ems-tnum">
                {fmtDisplay(yearTotals.qualified)}
              </span>
              <span className="ys-l">Qualified</span>
            </div>
            <div className="ems-year-stat">
              <span className="ys-v ems-tnum">{fmtDisplay(yearTotals.demo)}</span>
              <span className="ys-l">Demos</span>
            </div>
            <div className="ems-year-stat">
              <span className="ys-v ok ems-tnum">
                {fmtDisplay(yearTotals.enrolled)}
              </span>
              <span className="ys-l">Enrolled</span>
            </div>
            <div className="ems-year-stat">
              <span className="ys-v ems-tnum">
                {formatPercentShort(yearConversionPct)}
              </span>
              <span className="ys-l">Conversion</span>
            </div>
          </div>
        </div>

        {bestMonthRow ? (
          <div className="ems-card ems-lmr-best">
            <div className="ems-lmr-best-ic">
              <Trophy size={18} />
            </div>
            <div>
              <div className="ems-lmr-best-title">
                Best month: {bestMonthRow.full} {year}
              </div>
              <div className="ems-lmr-best-sub">
                {fmtDisplay(bestMonthRow.totals?.enrollment_this_month)} enrollments
                from {fmtDisplay(bestMonthRow.totals?.leads_this_month)} new leads (
                {formatPercentShort(getConversionPercent(bestMonthRow.totals))}{" "}
                conversion)
              </div>
            </div>
          </div>
        ) : null}

        {errorText ? (
          <div
            style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}
          >
            {errorText}
          </div>
        ) : null}

        <div className="ems-card ems-table-card">
          <div className="ems-table-wrap">
            {yearRowsLoading ? (
              <div className="ems-loading-screen">
                <CircularProgress size={28} />
              </div>
            ) : (
              <table className="ems-data ems-lmr-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="ems-num">New Leads</th>
                    <th className="ems-num">Qualified</th>
                    <th className="ems-num">Demos</th>
                    <th className="ems-num">Enrolled</th>
                    <th className="ems-num">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {QUARTERS.map((q) => (
                    <React.Fragment key={q.key}>
                      <tr className="ems-lmr-q-row">
                        <td colSpan={6}>
                          {q.key} - {year}
                        </td>
                      </tr>
                      {q.months.map((mm) => renderMonthRow(rowsByMonthId[mm]))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "LEADS",
      value: totals.leads_this_month,
      sub: "new this month",
      ok: false,
    },
    {
      label: "QUALIFIED",
      value: totals.qualified_this_month,
      sub: formatNumber(totals.leads_this_month) > 0
        ? `${formatPercentShort(formatNumber(totals.qualified_this_month) / formatNumber(totals.leads_this_month) * 100)} of leads`
        : "0.0% of leads",
      ok: false,
    },
    {
      label: "UNQUALIFIED",
      value: totals.unqualified_this_month,
      sub: formatNumber(totals.leads_this_month) > 0
        ? `${formatPercentShort(formatNumber(totals.unqualified_this_month) / formatNumber(totals.leads_this_month) * 100)} of leads`
        : "0.0% of leads",
      ok: false,
    },
    {
      label: "SPAM",
      value: totals.spam_this_month,
      sub: formatNumber(totals.leads_this_month) > 0
        ? `${formatPercentShort(formatNumber(totals.spam_this_month) / formatNumber(totals.leads_this_month) * 100)} of leads`
        : "0.0% of leads",
      ok: false,
    },
    {
      label: "DEMO",
      value: totals.demo_this_month,
      sub: formatNumber(totals.qualified_this_month) > 0
        ? `${formatPercentShort(formatNumber(totals.demo_this_month) / formatNumber(totals.qualified_this_month) * 100)} of qualified`
        : "0.0% of qualified",
      ok: false,
    },
    {
      label: "ENROLLED",
      value: totals.enrollment_this_month,
      sub: formatNumber(totals.demo_this_month) > 0
        ? `${formatPercentShort(formatNumber(totals.enrollment_this_month) / formatNumber(totals.demo_this_month) * 100)} of demos`
        : "0.0% of demos",
      ok: true,
    },
  ];

  const convPct = getConversionPercent(totals);

  return (
    <div className="ems-dashboard ems-fade-up" id="ems-lmr-print">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Lead Monthly Funnel Report</h1>
          <div className="ems-page-sub">
            Daily leads, stage counts, demos, enrollments and conversion for the selected month.
          </div>
        </div>
        <div className="ems-head-actions">
          <button type="button" className="ems-btn ems-ghost" onClick={() => window.print()}>
            <Printer size={15} /> Print
          </button>
          <button
            type="button"
            className="ems-btn ems-primary"
            onClick={downloadMonthPdf}
            disabled={loading || !report}
          >
            <Download size={15} /> Download PDF
          </button>
        </div>
      </div>

      <div className="ems-section-divider" />

      <div className="ems-card ems-lmr-filter-card">
        <button
          type="button"
          className="ems-btn ems-ghost"
          onClick={() => navigate("/leads-monthly-funnel-report")}
        >
          <ArrowLeft size={15} /> Back
        </button>
        <div className="ems-lmr-month-field">
          <label htmlFor="lmr-month-sel" className="ems-lmr-month-label">Month</label>
          <select
            id="lmr-month-sel"
            className="ems-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {buildMonthOptions(month).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="ems-btn ems-primary"
          disabled={loading || !month}
          onClick={() => {
            navigate(`/leads-monthly-funnel-report/${month}`);
            fetchReport(month);
          }}
        >
          {loading
            ? <CircularProgress size={14} sx={{ color: "inherit" }} />
            : <Eye size={15} />}
          Show
        </button>
        {(report?.month || report?.previous_month) && (
          <div className="ems-lmr-showing">
            {report.month && <>Showing: <strong>{report.month}</strong></>}
            {report.previous_month && <> &middot; Previous: <strong>{report.previous_month}</strong></>}
          </div>
        )}
      </div>

      {errorText && (
        <div style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}>
          {errorText}
        </div>
      )}

      <div className="ems-sec-head" style={{ marginTop: 20 }}>
        <div className="ems-sec-title"><span className="ems-bar" />Monthly Summary</div>
        {!loading && report && convPct > 0 && (
          <div className="ems-lmr-conv-badge">
            <TrendingUp size={14} /> {formatPercent(convPct)} conversion
          </div>
        )}
      </div>

      {loading ? (
        <div className="ems-loading-screen"><CircularProgress size={28} /></div>
      ) : report ? (
        <>
          <div className="ems-grid ems-g-6" style={{ marginBottom: 24 }}>
            {statCards.map((card) => (
              <div key={card.label} className="ems-card ems-lmr-stat">
                <div className="ems-lmr-stat-label">{card.label}</div>
                <div className={`ems-lmr-stat-value${card.ok ? " ok" : ""}`}>
                  {fmtDisplay(card.value)}
                </div>
                <div className={`ems-lmr-stat-bar${card.ok ? " ok" : ""}`} />
                <div className="ems-lmr-stat-sub">{card.sub}</div>
              </div>
            ))}
          </div>

          <div className="ems-card ems-table-card" style={{ marginBottom: 24 }}>
            <div className="ems-table-wrap">
              <table className="ems-data ems-lmr-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="ems-num">Leads</th>
                    <th className="ems-num">Qualified</th>
                    <th className="ems-num">Unqualified</th>
                    <th className="ems-num">Spam</th>
                    <th className="ems-num">Demo</th>
                    <th className="ems-num">Enrolled</th>
                    <th className="ems-num">Conversion %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="ems-lmr-q-row">
                    <td colSpan={8}>Year &middot; {selYear}</td>
                  </tr>
                  <tr>
                    <td className="ems-lmr-month">{monthFullName}</td>
                    <td className={`ems-num ems-lmr-num${formatNumber(totals.leads_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.leads_this_month)}
                    </td>
                    <td className={`ems-num ems-lmr-num${formatNumber(totals.qualified_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.qualified_this_month)}
                    </td>
                    <td className={`ems-num ems-lmr-num${formatNumber(totals.unqualified_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.unqualified_this_month)}
                    </td>
                    <td className={`ems-num ems-lmr-num${formatNumber(totals.spam_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.spam_this_month)}
                    </td>
                    <td className={`ems-num ems-lmr-num${formatNumber(totals.demo_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.demo_this_month)}
                    </td>
                    <td className={`ems-num ems-lmr-enrolled${formatNumber(totals.enrollment_this_month) > 0 ? "" : " is-zero"}`}>
                      {fmtDisplay(totals.enrollment_this_month)}
                    </td>
                    <td className="ems-num">
                      <div className="ems-conv">
                        <div className="ems-conv-track">
                          <div
                            className="ems-conv-fill"
                            style={{ width: `${Math.min(100, Math.max(0, convPct))}%` }}
                          />
                        </div>
                        <span className={`ems-conv-val${convPct > 0 ? " ok" : ""}`}>
                          {formatPercent(convPct)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="ems-sec-head">
            <div className="ems-sec-title"><span className="ems-bar" />Daily Report</div>
            <span className="ems-badge neutral">{dailyRows.length} days</span>
          </div>
          <div className="ems-card ems-table-card">
            <div className="ems-table-wrap">
              <table className="ems-data ems-lmr-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="ems-num">Leads</th>
                    <th className="ems-num">Qualified</th>
                    <th className="ems-num">Unqualified</th>
                    <th className="ems-num">Spam</th>
                    <th className="ems-num">Demo</th>
                    <th className="ems-num">Enrolled</th>
                    <th className="ems-num">Conversion %</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ textAlign: "center", color: "var(--ems-faint)", padding: "28px 0" }}
                      >
                        No data found.
                      </td>
                    </tr>
                  )}
                  {dailyRows.map((row, idx) => {
                    const dLeads = formatNumber(row.leads ?? 0);
                    const dQual = formatNumber(row.qualified ?? row.stage_counts?.Qualified ?? 0);
                    const dUnqual = formatNumber(row.unqualified ?? row.stage_counts?.Unqualified ?? 0);
                    const dSpam = formatNumber(row.spam ?? row.stage_counts?.Spam ?? 0);
                    const dDemo = formatNumber(row.demo ?? 0);
                    const dEnr = formatNumber(row.enrollment ?? row.admission_enrollment ?? row.stage_counts?.Enrolled ?? 0);
                    const dConv = formatNumber(row.conversion_percent ?? 0);
                    return (
                      <tr
                        key={`${row.date || row.date_label || idx}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const leadDate = getLeadDateParamFromDailyRow(row, month);
                          if (!leadDate) return;
                          navigate(`/leads?lead_date=${encodeURIComponent(leadDate)}`);
                        }}
                      >
                        <td className="ems-lmr-month">{row.date_label || row.date || "-"}</td>
                        <td className={`ems-num ems-lmr-num${dLeads > 0 ? "" : " is-zero"}`}>{fmtDisplay(dLeads)}</td>
                        <td className={`ems-num ems-lmr-num${dQual > 0 ? "" : " is-zero"}`}>{fmtDisplay(dQual)}</td>
                        <td className={`ems-num ems-lmr-num${dUnqual > 0 ? "" : " is-zero"}`}>{fmtDisplay(dUnqual)}</td>
                        <td className={`ems-num ems-lmr-num${dSpam > 0 ? "" : " is-zero"}`}>{fmtDisplay(dSpam)}</td>
                        <td className={`ems-num ems-lmr-num${dDemo > 0 ? "" : " is-zero"}`}>{fmtDisplay(dDemo)}</td>
                        <td className={`ems-num ems-lmr-enrolled${dEnr > 0 ? "" : " is-zero"}`}>{fmtDisplay(dEnr)}</td>
                        <td className="ems-num">
                          <div className="ems-conv">
                            <div className="ems-conv-track">
                              <div className="ems-conv-fill" style={{ width: `${Math.min(100, Math.max(0, dConv))}%` }} />
                            </div>
                            <span className={`ems-conv-val${dConv > 0 ? " ok" : ""}`}>
                              {formatPercent(dConv)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="ems-empty">
          <div className="ems-empty-icon"><Download size={28} /></div>
          <div>Select a month and click Show to load the report.</div>
        </div>
      )}
    </div>
  );
}
